// STABILITY-CRITICAL: mexe em cache offline/atualização de versão do PWA em produção;
// antes de alterar, leia docs/llm/slices/architecture-overview.md e alinhe com o
// responsável pelo sync.
//
// Este arquivo só REGISTRA o service worker e AVISA (onUpdate) quando há versão nova. Ele nunca
// deve ativar versão nova, apagar cache nem recarregar a página por conta própria: quem decide
// é utils/updateAnnouncer.ts, a pedido do usuário e com o sync parado.

import { recordError, recordEvent } from './observability/events'

// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://bit.ly/CRA-PWA

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  // [::1] is the IPv6 localhost address.
  window.location.hostname === '[::1]' ||
  // 127.0.0.0/8 are considered localhost for IPv4.
  window.location.hostname.match(
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
  ),
)

export function register(config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href)
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config)

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.log('Service worker ready (cache-first).')
          }
        })
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config)
      }
    })
  }
}

// Versão nova que já estava em espera quando o app abriu (aviso dispensado numa sessão anterior,
// ou instalada com o app fechado). Sem isto ela só entraria quando TODAS as abas fossem fechadas.
// Exportada para teste. Só anuncia (`onUpdate`); nunca ativa nem recarrega.
export function announceWaitingWorker(registration, config, controller) {
  if (!registration || !registration.waiting || !controller) return false
  if (config && config.onUpdate) {
    config.onUpdate(registration, { source: 'waiting_on_boot' })
  }
  return true
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      announceWaitingWorker(
        registration,
        config,
        navigator.serviceWorker.controller,
      )

      registration.onupdatefound = () => {
        const installingWorker = registration.installing
        if (installingWorker == null) {
          return
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              recordEvent({
                level: 'info',
                category: 'outro',
                action: 'service_worker_update_available',
              })

              if (config && config.onUpdate) {
                config.onUpdate(registration, { source: 'installed' })
              }
            } else {
              recordEvent({
                level: 'info',
                category: 'outro',
                action: 'service_worker_cached_for_offline',
              })

              if (config && config.onSuccess) {
                config.onSuccess(registration)
              }
            }
          }
        }
      }
    })
    .catch((error) => {
      console.error('[serviceWorker] registration failed', error)
      recordError(error, {
        category: 'outro',
        action: 'service_worker_registration_failed',
      })
    })
}

function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type')
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload()
          })
        })
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config)
      }
    })
    .catch(() => {
      recordEvent({
        level: 'info',
        category: 'sync',
        action: 'service_worker_check_offline',
      })
    })
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.unregister()
    })
  }
}
